import {Card, Col, Row} from 'antd';
import {CreateMasterAccountInput, useCreateMasterAccountMutation} from 'api/generated';
import {masterAccountsQuery} from 'api/queries';
import {BottomActions, CancelButton, IBANInput, Spacer} from 'components';
import {UrlInput} from 'components/Form/UrlInput';
import {PageHeader} from 'components/PageHeader';
import {Formik} from 'formik';
import {Form, FormItem, Input, SubmitButton} from 'formik-antd';
import {useMediaQuery} from 'hooks/useMediaQuery';
import * as iban from 'ibantools';
import {omitEmpty} from 'lib/omitEmpty';
import React from 'react';
import {useHistory} from 'react-router-dom';
import * as yup from 'yup';

type FormValues = {
  name: string;
  adminEmail: string;
  business: {
    legalName: string;
    website: string;
    documentNumber: string;
    servicesDescription: string;
  };
  promoCode: string;
  iban: string;
};

const query = {
  query: masterAccountsQuery,
  variables: {
    from: 0,
    size: 20,
    filter: {}
  }
};

export const CreateMasterAccount: React.FC<any> = () => {
  const {isTablet} = useMediaQuery();
  const history = useHistory();
  const [createMasterAccount] = useCreateMasterAccountMutation({
    updateQuery: query
  });

  const validationSchema = yup.object().shape({
    name: yup.string().required('Name is required'),
    adminEmail: yup.string().email('Invalid email').required('Admin email is required'),
    business: yup.object().shape({
      legalName: yup.string().required('Legal name is required'),
      website: yup.string().url('Invalid website URL').required('Website is required'),
      documentNumber: yup.string().required('Tax Identification Number is required'),
      servicesDescription: yup.string().required('Services Description is required')
    }),
    promoCode: yup.string().required('Promo code is required'),
    iban: yup.string().test('iban', 'Invalid IBAN', (value) => !value || iban.isValidIBAN(value!))
  });

  const initialValues: FormValues = {
    name: '',
    adminEmail: '',
    business: {
      legalName: '',
      website: '',
      documentNumber: '',
      servicesDescription: ''
    },
    promoCode: '',
    iban: ''
  };

  const handleSubmit = async (input: CreateMasterAccountInput) => {
    if (!input) {
      return;
    }
    const result = await createMasterAccount({
      variables: {
        input: omitEmpty(input)
      },
      refetchQueries: [query]
    });
    const id = result.data?.createMasterAccount?.id;
    if (id) {
      history.push(`/master-accounts/${id}`);
    }
  };

  return (
    <Row>
      <Col span={isTablet ? 24 : 12} offset={isTablet ? 0 : 6}>
        <PageHeader title="Create master account" />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}>
          <Form layout="vertical">
            <Card>
              <FormItem hasFeedback={false} name="name" label="Name">
                <Input name="name" placeholder="My name" />
              </FormItem>
              <FormItem hasFeedback={false} name="adminEmail" label="Admin email">
                <Input name="adminEmail" type="email" placeholder="john@doe.com" />
              </FormItem>
              <FormItem hasFeedback={false} name="business.legalName" label="Business name">
                <Input name="business.legalName" placeholder="My Company" />
              </FormItem>
              <FormItem hasFeedback={false} name="business.website" label="Website">
                <UrlInput name="business.website" placeholder="mycompany.com" />
              </FormItem>
              <FormItem
                hasFeedback={false}
                name="business.documentNumber"
                label="Tax Identification Number (CIF)">
                <Input name="business.documentNumber" placeholder="1234567889" />
              </FormItem>
              <FormItem
                hasFeedback={false}
                name="business.servicesDescription"
                label="Services Description">
                <Input.TextArea
                  name="business.servicesDescription"
                  placeholder="We sell shoes online in the e-commerce store."
                />{' '}
              </FormItem>
              <FormItem hasFeedback={false} name="promoCode" label="Promo code">
                <Input name="promoCode" />
              </FormItem>
              <FormItem hasFeedback={false} name="iban" label="IBAN">
                <IBANInput name="iban" countryCode={'ES'} />
              </FormItem>
              <input type="submit" value="submit" hidden />
            </Card>

            <BottomActions>
              <CancelButton backPath={'/master-accounts'}>{'Cancel'}</CancelButton>
              <Spacer />
              <SubmitButton>Create master account</SubmitButton>
            </BottomActions>
          </Form>
        </Formik>
      </Col>
    </Row>
  );
};
